import { useEffect, useRef, useState } from "react";
import { WHATSAPP_LINK, WHATSAPP_NUMBER } from "./constants";

// Floating WhatsApp widget shown on every page. No tracking, no cookies, no
// third-party scripts — it only opens wa.me on click. Ported from the original
// static site's site.js widget.
const WA_GLYPH = (
  <svg className="wa-glyph" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
    />
  </svg>
);

const CLOSE_ICON = (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export function WhatsAppWidget() {
  const digits = String(WHATSAPP_NUMBER).replace(/\D/g, "");
  const valid = /^[1-9]\d{7,14}$/.test(digits);
  const waUrl = WHATSAPP_LINK;

  const [open, setOpen] = useState(false);
  const [tucked, setTucked] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!valid) return;
    const small = window.matchMedia("(max-width: 860px)");
    const isField = (el: EventTarget | null) =>
      el instanceof Element && el.matches("input, select, textarea") && !rootRef.current?.contains(el);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    // On small screens, step out of the way while the visitor types in a form.
    const onFocusIn = (e: FocusEvent) => {
      if (small.matches && isField(e.target)) {
        setOpen(false);
        setTucked(true);
      }
    };
    const onFocusOut = (e: FocusEvent) => {
      if (isField(e.target) && !isField((e as FocusEvent & { relatedTarget: EventTarget | null }).relatedTarget)) {
        setTucked(false);
      }
    };

    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, [valid]);

  if (!valid) return null; // placeholder / invalid number: show nothing

  const cls = ["wa-widget", open ? "is-open" : "", tucked ? "is-tucked" : ""].filter(Boolean).join(" ");

  return (
    <div className={cls} ref={rootRef}>
      <button
        className="wa-launcher"
        type="button"
        ref={launcherRef}
        aria-expanded={open}
        aria-controls="wa-panel"
        aria-label="Chat with Quintara on WhatsApp"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="wa-launcher-open">{WA_GLYPH}</span>
        <span className="wa-launcher-close">{CLOSE_ICON}</span>
      </button>
      <div className="wa-panel" id="wa-panel" role="region" aria-labelledby="wa-title" hidden={!open}>
        <button
          className="wa-close"
          type="button"
          aria-label="Close WhatsApp chat panel"
          onClick={() => {
            setOpen(false);
            launcherRef.current?.focus();
          }}
        >
          {CLOSE_ICON}
        </button>
        <div className="wa-head">
          <span className="wa-avatar" aria-hidden="true"></span>
          <p className="wa-title" id="wa-title">
            Chat with Quintara
          </p>
        </div>
        <p className="wa-text">Have a question? Talk to our team on WhatsApp.</p>
        <img className="wa-barcode" src="/assets/barcode.png" alt="Scan to chat with Quintara on WhatsApp" width={160} height={160} />
        <a className="btn btn-primary wa-cta" href={waUrl} target="_blank" rel="noopener noreferrer">
          {WA_GLYPH}
          <span>Start WhatsApp chat</span>
        </a>
      </div>
    </div>
  );
}
